'use client';
// ═══════════════════════════════════════════════════════════════════════════
// « EST-CE QUE JE PAIE ASSEZ ? » — ce qu'un PDF ne fera jamais
// ───────────────────────────────────────────────────────────────────────────
// Sur « barème CP … », les cinq premiers résultats de Google sont des PDF
// (FGTB, CSC, CGSLB, fiche Limosa du SPF). Ils donnent un tableau. Aucun ne
// répond à la question que l'employeur se pose vraiment : « et MOI ? ».
// C'est là qu'une page vivante gagne — et le calcul existait déjà dans l'app,
// il ne sortait simplement pas (règle « toujours brancher ce qui est codé »).
//
// Le verdict ne dit JAMAIS « vous êtes en règle » : le minimum publié vaut pour
// 0 an d'ancienneté et la catégorie la plus basse, donc un travailleur avec de
// l'ancienneté a droit à plus. Il dit « au-dessus du minimum d'entrée », qui est
// la seule chose que la donnée permet d'affirmer (R2 bis).
//
// FR + NL (R8). Composant défini au niveau du MODULE, jamais dans un return
// (R4) — sinon les champs perdent le focus à chaque frappe.
// ═══════════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { verifierSalaire } from '@/app/lib/verif-salaire-minimum';

const G = '#c9a961';

const euro = (n) =>
  Number(n).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const T = {
  fr: {
    titre: (cp) => `Est-ce que je paie assez en CP ${cp} ?`,
    intro:
      'Tapez le brut mensuel que vous versez : nous le comparons au minimum sectoriel de cette commission paritaire et au minimum national, et nous retenons le plus élevé des deux.',
    brut: 'Brut mensuel versé',
    regime: 'Régime',
    plein: 'Temps plein',
    partiel: 'Temps partiel',
    heuresT: 'Heures par semaine',
    heuresR: 'Temps plein du secteur',
    heuresRAide: 'à vérifier dans votre CCT',
    bouton: 'Comparer',
    sousMin: 'En dessous du minimum',
    auMin: 'Exactement au minimum',
    auDessus: 'Au-dessus du minimum d’entrée',
    minApplicable: (m) => `Minimum applicable : ${euro(m)}`,
    manque: (e) => `Il manque ${euro(Math.abs(e))} par mois.`,
    marge: (e) => `Marge : ${euro(e)} par mois.`,
    prorataDit: (h, r) => `Calculé au prorata de ${h} h sur ${r} h.`,
    plancherSecteur: 'C’est le barème sectoriel qui s’impose ici.',
    plancherNational: 'Ici c’est le minimum national (RMMMG) qui s’impose : il est plus élevé que le barème sectoriel.',
    reserve:
      'Ce minimum vaut à l’engagement, pour la catégorie la plus basse et sans ancienneté. Un travailleur plus ancien ou d’une fonction supérieure a droit à davantage — la grille complète est dans le tableau ci-dessus.',
    aide: 'Faire vérifier ce dossier',
    sansBareme:
      'Aucun barème n’est publié pour cette commission paritaire, et nous n’en inventons pas. Le minimum national reste votre plancher.',
    incomplet: 'Indiquez un brut mensuel pour obtenir la comparaison.',
  },
  nl: {
    titre: (cp) => `Betaal ik genoeg in PC ${cp} ?`,
    intro:
      'Geef het bruto maandloon in dat u uitbetaalt: wij vergelijken het met het sectorale minimum van dit paritair comité en met het nationale minimum, en houden het hoogste van de twee aan.',
    brut: 'Uitbetaald bruto maandloon',
    regime: 'Regime',
    plein: 'Voltijds',
    partiel: 'Deeltijds',
    heuresT: 'Uren per week',
    heuresR: 'Voltijds in de sector',
    heuresRAide: 'na te kijken in uw cao',
    bouton: 'Vergelijken',
    sousMin: 'Onder het minimum',
    auMin: 'Exact op het minimum',
    auDessus: 'Boven het instapminimum',
    minApplicable: (m) => `Toepasselijk minimum: ${euro(m)}`,
    manque: (e) => `Er ontbreekt ${euro(Math.abs(e))} per maand.`,
    marge: (e) => `Marge: ${euro(e)} per maand.`,
    prorataDit: (h, r) => `Berekend pro rata ${h} u op ${r} u.`,
    plancherSecteur: 'Hier geldt het sectorale barema.',
    plancherNational: 'Hier geldt het nationale minimum (GGMMI): het ligt hoger dan het sectorale barema.',
    reserve:
      'Dit minimum geldt bij indiensttreding, voor de laagste categorie en zonder anciënniteit. Een werknemer met meer anciënniteit of een hogere functie heeft recht op meer — de volledige tabel staat hierboven.',
    aide: 'Dit dossier laten nakijken',
    sansBareme:
      'Voor dit paritair comité is geen barema gepubliceerd, en wij verzinnen er geen. Het nationale minimum blijft uw ondergrens.',
    incomplet: 'Geef een bruto maandloon in om te vergelijken.',
  },
};

const COULEURS = {
  'sous-le-minimum': { fond: '#fdf2f0', bord: '#9a3b2e', texte: '#7d2f24' },
  'au-minimum': { fond: '#fdfaf0', bord: '#b08a3e', texte: '#7d6128' },
  'au-dessus': { fond: '#f2f7f2', bord: '#4a8a4a', texte: '#2c5c2c' },
  'sans-bareme': { fond: '#f7f6f3', bord: '#8a8578', texte: '#55524a' },
  incomplet: { fond: '#f7f6f3', bord: '#8a8578', texte: '#55524a' },
};

export default function VerifSalaire({ cpKey, lang = 'fr', minimumSecteur, rmmmg, heuresReference }) {
  const t = T[lang] || T.fr;
  const [brut, setBrut] = useState('');
  const [partiel, setPartiel] = useState(false);
  const [heuresT, setHeuresT] = useState('');
  const [heuresR, setHeuresR] = useState(heuresReference ? String(heuresReference) : '');
  const [res, setRes] = useState(null);

  function comparer(e) {
    e.preventDefault();
    setRes(
      verifierSalaire({
        brutMensuel: parseFloat(String(brut).replace(',', '.')),
        minimumSecteur,
        rmmmg,
        heuresTravaillees: partiel ? parseFloat(String(heuresT).replace(',', '.')) : null,
        heuresReference: partiel ? parseFloat(String(heuresR).replace(',', '.')) : null,
      })
    );
  }

  const c = res ? COULEURS[res.statut] || COULEURS.incomplet : null;
  const titreVerdict =
    res?.statut === 'sous-le-minimum' ? t.sousMin
      : res?.statut === 'au-minimum' ? t.auMin
      : res?.statut === 'au-dessus' ? t.auDessus
      : null;

  return (
    <div style={{ maxWidth: 720, margin: '28px auto 0', background: '#fff', border: '1px solid #e8e1cd', borderRadius: 10, padding: 20, borderLeft: `3px solid ${G}` }}>
      <h2 style={{ fontSize: 17, fontWeight: 800, color: '#1a1a18', margin: '0 0 8px' }}>{t.titre(cpKey)}</h2>
      <p style={{ fontSize: 14.5, lineHeight: 1.6, color: '#4a463c', margin: '0 0 14px' }}>{t.intro}</p>

      <form onSubmit={comparer}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 190px', minWidth: 0, fontSize: 12.5, color: '#6a6558' }}>
            {t.brut}
            <input
              type="text" inputMode="decimal" value={brut} onChange={(ev) => setBrut(ev.target.value)}
              placeholder="2 400,00"
              style={{ width: '100%', marginTop: 4, padding: '10px 12px', border: '1px solid #ddd4bc', borderRadius: 8, fontSize: 15 }}
            />
          </label>
          <label style={{ flex: '0 1 150px', fontSize: 12.5, color: '#6a6558' }}>
            {t.regime}
            <select
              value={partiel ? 'p' : 'f'} onChange={(ev) => setPartiel(ev.target.value === 'p')}
              style={{ width: '100%', marginTop: 4, padding: '10px 12px', border: '1px solid #ddd4bc', borderRadius: 8, fontSize: 15, background: '#fff' }}
            >
              <option value="f">{t.plein}</option>
              <option value="p">{t.partiel}</option>
            </select>
          </label>
        </div>

        {partiel && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
            <label style={{ flex: '1 1 140px', fontSize: 12.5, color: '#6a6558' }}>
              {t.heuresT}
              <input
                type="text" inputMode="decimal" value={heuresT} onChange={(ev) => setHeuresT(ev.target.value)}
                placeholder="20"
                style={{ width: '100%', marginTop: 4, padding: '10px 12px', border: '1px solid #ddd4bc', borderRadius: 8, fontSize: 15 }}
              />
            </label>
            <label style={{ flex: '1 1 140px', fontSize: 12.5, color: '#6a6558' }}>
              {t.heuresR} <span style={{ color: '#9a9384' }}>({t.heuresRAide})</span>
              <input
                type="text" inputMode="decimal" value={heuresR} onChange={(ev) => setHeuresR(ev.target.value)}
                placeholder="38"
                style={{ width: '100%', marginTop: 4, padding: '10px 12px', border: '1px solid #ddd4bc', borderRadius: 8, fontSize: 15 }}
              />
            </label>
          </div>
        )}

        <button
          type="submit"
          style={{ marginTop: 12, padding: '11px 22px', background: '#1a1a18', color: '#f5f0e4', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
        >
          {t.bouton}
        </button>
      </form>

      {res && (
        <div role="status" style={{ marginTop: 16, background: c.fond, borderLeft: `3px solid ${c.bord}`, borderRadius: 8, padding: '14px 16px' }}>
          {res.statut === 'sans-bareme' ? (
            <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: c.texte }}>{t.sansBareme}</p>
          ) : res.statut === 'incomplet' ? (
            <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: c.texte }}>{t.incomplet}</p>
          ) : (
            <>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: c.texte }}>{titreVerdict}</p>
              <p style={{ margin: '6px 0 0', fontSize: 14.5, lineHeight: 1.6, color: c.texte }}>
                {t.minApplicable(res.minimumApplicable)}
                {res.statut === 'sous-le-minimum' ? ` — ${t.manque(res.ecart)}` : ''}
                {res.statut === 'au-dessus' ? ` — ${t.marge(res.ecart)}` : ''}
              </p>
              {res.prorata != null && (
                <p style={{ margin: '4px 0 0', fontSize: 13, color: c.texte, opacity: 0.85 }}>
                  {t.prorataDit(heuresT, heuresR)}
                </p>
              )}
              <p style={{ margin: '4px 0 0', fontSize: 13, color: c.texte, opacity: 0.85 }}>
                {res.plancherRetenu === 'national' ? t.plancherNational : t.plancherSecteur}
              </p>
            </>
          )}
          <p style={{ margin: '10px 0 0', fontSize: 12.5, lineHeight: 1.6, color: '#6a6558' }}>{t.reserve}</p>
          {res.statut === 'sous-le-minimum' && (
            <p style={{ margin: '10px 0 0' }}>
              <a href="/contact" style={{ color: '#8a6f2e', fontWeight: 700, fontSize: 14.5 }}>{t.aide} →</a>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
